import moment from 'moment';
import csv from 'csvtojson';
import { isEmpty, pick, partition } from 'lodash';
import { Parser } from 'json2csv';
import { hashCode } from '../../utils/hashCode';
import { downloadFile } from '../../utils/file';
import { isDelivering } from '../../utils/adManagerHelpers';
import { computeTotalContractAmount } from '../../utils/totalContractAmount';
import {
  hasMixedVideoAndNonVideoProducts,
  mixedVideoAndNonVideoMessage
} from '../../utils/creativeProductValidation';
import { countLineItems, lineItemCountForSalesProduct } from '../../utils/countLineItems';
import { hasPubSelect } from '../../utils/hasPubSelect';
import fs from "fs";

/**
 * Prepares and downloads CSV file containing all placements for the order
 * Arrays of objects are converted to ;-separated lists
 * If there are no placements, we put out a blank template
 * @param {Object} order Order
 * @param {Array} placements List of placements to export
 */
export const exportPlacements = (order, placements) => {
  const filename = `Galileo-${order.id.toString()}-Agency-${order.agencyOrderId}.csv`;
  let values = [];
  if (order.Placements.length === placements.length && order.Packages && order.Packages.length > 0) {
    values = exportByPackage(order);
  } else if (placements && placements.length > 0) {
    values = placements.map(p => getValuesFromPlacement(order, p.id));
  }
  const importList = createImportList(order);
  const columns = values.length > 0 ? Object.keys(values[0]) : importList.map(il => il.field);
  const csvParser = new Parser({ fields: columns });
  const csvString = csvParser.parse(values);
  const newString = makeFriendlyTitles(csvString, importList);
  downloadFile(newString, 'text/csv', filename, false);
};

/**
 * If an order has packages, we export those ahead of their placements.
 * Sorting by package name, then ID does this.
 * @param order
 * @returns {*[]}
 */
const exportByPackage = (order) => {
  let pkgValues = [];
  let plValues = [];
  if (order.Packages && order.Packages.length > 0) {
    pkgValues = order.Packages.map(pkg => getValuesFromPackage(pkg, order)).filter(Boolean);
  }
  if (order.Placements && order.Placements.length > 0) {
    plValues = order.Placements.map(pl => {
      return getValuesFromPlacement(order, pl.id)
    }).filter(Boolean);
  }
  return sortRows(plValues.concat(pkgValues));
};

/**
 * Sort packages and placements together.
 * Get all rows per package, in package id order.
 * package is first row followed by placements ordered by id.
 * Se
 * @param values
 * @returns {*}
 */
const sortRows = values => {
  const valuesInPkg = values.filter(v => v.packageId);
  const valuesNotInPkg = values.filter(v => !v.packageId);
  let lowValues = [];
  let topValues = [];
  if (valuesInPkg.length > 0) {
    topValues = valuesInPkg.sort((a, b) => {
      if (a.packageId < b.packageId) {
        return -1;
      }
      if (a.packageId > b.packageId) {
        return 1;
      }
      if (a.packageId === a.id) {
        return -1;
      }
      if (a.id < b.id) {
        return -1;
      }
      if (a.id > b.id) {
        return 1;
      }
      return 0;
    });
  }
  if (valuesNotInPkg.length > 0) {
    lowValues = valuesNotInPkg.sort((a, b) => {
      if (a.id < b.id) {
        return -1;
      }
      if (a.id > b.id) {
        return 1;
      }
      return 0;
    });
  }
  return topValues.concat(lowValues);
}

const makeFriendlyTitles = (csvString, importList) => {
  const csvArray = csvString.split('\n');
  let titleRow = csvArray[0]
  const replaceLength = titleRow.length;
  importList.forEach(il => {
    titleRow = titleRow.replace(il.field, il.title)
  });
  return titleRow + csvString.slice(replaceLength);
};

/**
 * Package dates are calculated from all the child placements
 * The first row (which is a package) determines the titles, so we need to populate a lot of empty fields here
 * @param pkg
 * @param order
 * @returns {{}|null}
 */
const getValuesFromPackage = (pkg, order) => {
  if (!pkg.id) { // it's a placement, forget it
    return null;
  }
  const pkgPlacements = order.Placements.filter(pl => pl.packageId === pkg.id)
  let startDate = 'TBD';
  let endDate = 'TBD';
  if (pkgPlacements.length > 0) {
    startDate = pkgPlacements.reduce((m, p) => m < p.start ? m : p.start, pkgPlacements[0].start);
    endDate = pkgPlacements.reduce((m, p) => m > p.end ? m : p.end, pkgPlacements[0].end);
  }
  const pv = {};
  pv.id = pkg.id;
  pv.cookie = hashCode(order.id.toString() + pkg.id.toString());
  pv.type = 'PKG *****';
  pv.agencyPackageName = pkg.name;
  pv.ioRateType = pkg.rateType;
  pv.ioRate = pkg.rate;
  pv.contractedQuantity = pkg.contractedQuantity;
  pv.agencyPlacementId = '';
  pv.agencyBillingId = '';
  pv.agencyPlacementName = '';
  pv.start = moment(startDate).isValid() ? moment(startDate).format("YYYY-MM-DD") : '';
  pv.end = moment(endDate).isValid() ? moment(endDate).format("YYYY-MM-DD") : '';
  pv.totalContractAmount = pkg.cost;
  pv.comments = pkg.adNameComments;
  pv.billingGroup = '';
  pv.strategy = '';
  pv.salesProduct = '';
  pv.deviceTypes = '';
  pv.creativeProducts = '';
  pv.customCriteriaPresets = '';
  pv.viewability = '';
  pv.packageType = '';
  pv.targetLanguage = '';
  pv.gComments = '';
  pv.geoGlobal = '';
  pv.dmas = '';
  pv.states = '';
  pv.provinces = '';
  pv.territories = '';
  pv.neighborhoods = '';
  pv.cities = '';
  pv.counties = '';
  pv.regions = '';
  pv.countries = '';
  pv.geotargets = '';
  pv.thirdPartyFees = '';
  pv.creativePremiumPercent = '';
  pv.impressionsLimit = '';
  pv.exclusions = '';
  pv.pubs = '';
  pv.customTargeting = '';
  pv.customTargetingArr = '';
  pv.excludedGeotargetIds = '';
  pv.dfpLineItemId = '';
  pv.dfpLineItemName = '';
  pv.packageId = pv.id;
  return pv;
};

const getValuesFromPlacement = (order, plId) => {
  const SEPARATOR = ';';
  const foundPlacement = order.Placements.find(pl => pl.id === plId);
  if (!foundPlacement) {
    throw new Error(`placements ${plId} doesn't exist in order!, cannot export!`);
  }
  const pl = Object.assign({}, foundPlacement);
  const pv = {};
  pv.id = pl.id;
  pv.cookie = hashCode(order.id.toString() + pl.id.toString());
  pv.type = '';
  pv.agencyPackageName = pl.agencyPackageName || '~';
  pv.ioRateType = pl.IoRateType.name || 'CPM';
  pv.ioRate = pl.ioRate;
  pv.contractedQuantity = pl.contractedQuantity;
  pv.agencyPlacementId = `_${pl.agencyPlacementId}`;
  pv.agencyBillingId = `_${pl.agencyBillingId}`;
  pv.agencyPlacementName = pl.agencyPlacementName;
  pv.start = pl.start.slice(0, 10);
  pv.end = pl.end.slice(0, 10);
  const ret = computeTotalContractAmount(pl.contractedQuantity, pl.ioRate, pl.IoRateTypeId, pl.start, pl.end, pl.lineItemType);
  pv.totalContractAmount = ret.total;
  pv.comments = pl.comments;
  pv.billingGroup = pl.billingGroup;
  pv.strategy = isEmpty(pl.Strategy) ? '' : pl.Strategy.name;
  pv.salesProduct = isEmpty(pl.SalesProduct) ? '' : pl.SalesProduct.name;
  pv.deviceTypes = pl.DeviceTypes.map(obj => obj.name).join(SEPARATOR);
  pv.creativeProducts = pl.CreativeProducts.map(obj => obj.name).join(SEPARATOR);
  pv.customCriteriaPresets = pl.CustomCriteriaPresets.map(obj => obj.name).join(SEPARATOR);
  pv.viewability = pl.viewability;
  pv.packageType = pl.packageType;
  pv.targetLanguage = pl.targetLanguage;
  pv.gComments = pl.gComments;
  if (pl.geoGlobal) {
    pv.geoGlobal = pl.geoGlobal;
    pv.dmas = '';
    pv.states = '';
    pv.provinces = '';
    pv.territories = '';
    pv.neighborhoods = '';
    pv.cities = '';
    pv.counties = '';
    pv.regions = '';
    pv.countries = '';
    pv.geotargets = '';
    pv.excludedGeotargetIds = '';
  } else {
    pv.geoGlobal = '';
    const geos = exportGeotargets(pl.Geotargets);
    pv.dmas = geos.dmas;
    pv.states = geos.states;
    pv.provinces = geos.provinces;
    pv.territories = geos.territories;
    pv.neighborhoods = geos.neighborhoods;
    pv.cities = geos.cities;
    pv.counties = geos.counties;
    pv.regions = geos.regions;
    pv.countries = geos.countries;
    pv.geotargets = geos.other;
    pv.excludedGeotargetIds = pl.excludedGeotargetIds ? JSON.stringify(pl.excludedGeotargetIds) : '';
  }
  pv.thirdPartyFees = pl.DataFees.map(df => {
    const vendor = df.DataFeeType.name.split(' - ')[1];
    return `${vendor}=${df.amount}`
  }).join(SEPARATOR);
  pv.creativePremiumPercent = pl.creativePremiumPercent;
  pv.impressionsLimit = pl.impressionsLimit;
  pv.exclusions = (pl.exclusions) ? pl.exclusions.map(exc => exc.name).join(SEPARATOR) : '';
  pv.pubs = (pl.Pubs) ? pl.Pubs.map(pub => pub.name).join(SEPARATOR) : '';
  const pli = (pl.LineItems && pl.LineItems[0]) ? pl.LineItems[0] : { adManagerLineitemId: '', adManagerPayload: { name: '' } };
  pv.customTargeting = pl.customTargeting;
  pv.customTargetingArr = pl.customTargetingArr ? JSON.stringify(pl.customTargetingArr) : '';
  pv.dfpLineItemId = pli.adManagerLineitemId;
  pv.dfpLineItemName = pli.adManagerPayload.name;
  pv.packageId = pl.packageId;
  return pv;
};

/**
 * Split geotargets into the ones we will import and everything else
 * @param geos
 * @returns {{dmas: *, states: *, provinces: *, territories: *, neighborhoods: *, cities: string, counties: string, regions: *, countries: *, other: *}}
 */
const exportGeotargets = geos => {
  const SEPARATOR = ';';
  if (!geos) return '';
  const dmas = geos.filter(g => g.type === 'DMA_REGION').map(g => g.name).join(SEPARATOR);
  const states = geos.filter(g => g.type === 'STATE').map(g => g.name).join(SEPARATOR);
  const provinces = geos.filter(g => g.type === 'PROVINCE').map(g => g.name).join(SEPARATOR);
  const territories = geos.filter(g => g.type === 'TERRITORY').map(g => g.name).join(SEPARATOR);
  const neighborhoods = geos.filter(g => g.type === 'NEIGHBORHOOD').map(g => g.name).join(SEPARATOR);
  const cities = geos.filter(g => g.type === 'CITY').map(g => g.canonicalName).join(SEPARATOR).replaceAll(', ', '@');
  const counties = geos.filter(g => g.type === 'COUNTY').map(g => g.canonicalName).join(SEPARATOR).replaceAll(', ', '@');
  const regions = geos.filter(g => g.type === 'REGION').map(g => g.name).join(SEPARATOR);
  const countries = geos.filter(g => g.type === 'COUNTRY').map(g => g.name).join(SEPARATOR);
  const other = geos.filter(g => g.type !== 'STATE' && g.type !== 'DMA_REGION' && g.type !== 'PROVINCE' &&
    g.type !== 'TERRITORY' && g.type !== 'NEIGHBORHOOD' &&g.type !== 'COUNTY' && g.type !== 'CITY' &&
    g.type !== 'COUNTRY' && g.type !== 'REGION').map(g => g.name).join(SEPARATOR);
  return { dmas, states, provinces, territories, neighborhoods, cities, counties, regions, countries, other };
}

/**
 * Handles validation and import of placements from CSV file
 * @param {Object} order Order
 * @param {func} onComplete Callback called when import data has been prepared and validated
 * @param {func} toggleImportExport Toggle Import/Export dialog function to close on success
 * @param {Object} constants all the static values
 * @param strategies
 * @param deviceTypes
 * @param salesProducts
 * @param creativeProducts
 * @param presets
 * @param geos
 * @param isTest indicates if its unit test, which has no DOM and will save file locally
 * @returns {func} FileReader onloadend handler
 */
export const importPlacements = (
  order,
  onComplete,
  toggleImportExport,
  constants,
  strategies,
  deviceTypes,
  salesProducts,
  creativeProducts,
  presets,
  geos,
  isTest) => async (evt) => {
  const filename = `Galileo-${order.id.toString()}-Agency-${order.agencyOrderId}.ERRORS.csv`
  const csvString = evt.target.result;
  if (!csvString) {
    onComplete({ errors: ['Cannot import: does import file have correct data?'] });
    return;
  }
  // const csvArray = csvString.split('\n');
  // const titleRow = csvArray[0];
  const importList = createImportList(order);
  const imps = unmakeFriendlyTitles(csvString, importList);
  const rawImports = await csv().fromString(imps);
  const imports = rawImports.filter(ip => !Object.values(ip).every(val => val === '')); //ignore blank lines
  let results;
  try {
    results = processImports(order, imports, constants, strategies, deviceTypes, salesProducts, creativeProducts, presets, geos, importList);
    console.log('import placement errors', results);
    if (results.importErrors.count > 0) {
      const csvParser = new Parser({ fields: importList.map(il => il.field) });
      const csvString = csvParser.parse(rawImports);
      const newString = makeFriendlyTitles(csvString, importList);
      if (!isTest) {
        downloadFile(newString, 'text/csv', filename, false);
      } else {
        fs.writeFileSync('./test/importFile.ERRORS.csv', newString);
      }
      console.log(`import errors bad lines ${results.importErrors.badLines}`);
      console.log(`count ${ results.importErrors.count}`)
      onComplete(parseErrors(results.importErrors));
      console.log('import errors', results.importErrors);
    } else {
      const placements = results.imports.map(imp => ({
        newPlacement: imp,
        oldPlacement: order.Placements && order.Placements.length > 0 && imp.id ? order.Placements.find(p => p.id === imp.id) : []
      }));
      onComplete({ placements, errors: [], packages: results.packages });
      toggleImportExport();
    }
  } catch(e) {
    console.log('import placements', e);
    onComplete({ errors: ['Cannot import: is import file a CSV with all required fields and no extraneous data?'] });
    if (results.importErrors){
      console.log('import errors', results.importErrors);
    }
    console.log('results', results);
  }
};

const unmakeFriendlyTitles = (csvString, importList) => {
  const csvArray = csvString.split('\n');
  let titleRow = csvArray[0];
  const replaceLength = titleRow.length;
  importList.forEach(il => {
    titleRow = titleRow.replace(il.title, il.field)
  });
  return titleRow + csvString.slice(replaceLength);
};

/**
 * Programmatic has a few fewer fields than standard order
 * @param order
 * @returns {string[]}
 */
const createImportList = (order) => {
  let importList = [
    { field: 'id', title: 'Id' },
    { field: 'cookie', title: 'Cookie' },
    { field: 'type', title: 'Type' },
    { field: 'agencyPackageName', title: 'Agency Package Name' },
    { field: 'ioRateType', title: 'IO Rate Type' },
    { field: 'ioRate', title: 'IO Rate' },
    { field: 'contractedQuantity', title: 'Contracted Quantity' },
    { field: 'agencyPlacementId', title: 'Agency Placement Id' },
    { field: 'agencyBillingId', title: 'Agency Billing Id' },
    { field: 'agencyPlacementName', title: 'Agency Placement Name' },
    { field: 'start', title: 'Start Date' },
    { field: 'end', title: 'End Date' },
    { field: 'billingGroup', title: 'Billing Group' },
    { field: 'strategy', title: 'Strategy' },
    { field: 'salesProduct', title: 'Sales Product' },
    { field: 'deviceTypes', title: 'Device Types' },
    { field: 'creativeProducts', title: 'Creative Products' },
    { field: 'customCriteriaPresets', title: 'Standard Targeting' },
    { field: 'viewability', title: 'Viewability' },
    { field: 'packageType', title: 'Package Type' },
    { field: 'targetLanguage', title: 'Target Language' },
    { field: 'comments', title: 'Ad Name Comments' },
    { field: 'gComments', title: 'Galileo Comments' },
    { field: 'geoGlobal', title: 'Global Targeting' },
    { field: 'dmas', title: 'DMAs' },
    { field: 'states', title: 'States' },
    { field: 'provinces', title: 'Provinces' },
    { field: 'territories', title: 'Territories' },
    { field: 'neighborhoods', title: 'Neighborhoods' },
    { field: 'cities', title: 'Cities' },
    { field: 'counties', title: 'Counties' },
    { field: 'regions', title: 'Regions' },
    { field: 'countries', title: 'Countries' },
    { field: 'geotargets', title: 'Other Geotargets' },
    { field: 'creativePremiumPercent', title: 'Creative Premium Percent' },
    { field: 'impressionsLimit', title: 'Impressions Limit' },
    { field: 'thirdPartyFees', title: 'Third Party Fees' },
    { field: 'exclusions', title: 'Exclusions' },
    { field: 'pubs', title: 'Publications' },
    { field: 'customTargeting', title: 'Custom Targeting' },
    { field: 'customTargetingArr', title: 'CT List' },
    { field: 'excludedGeotargetIds', title: 'Excluded GeoTargets' },
  ];
  const nonProgrammaticFields = [
    'agencyPlacementId',
    'agencyBillingId',
    'billingGroup',
    'viewability'
  ];
  if (order && order.orderType === 'Programmatic') {
    importList = importList.filter(il => !nonProgrammaticFields.includes(il.field));
  }
  importList.push({ field: 'errors', title: 'Errors' });
  return importList;
}

/**
 * Sets error messages for found import errors
 * @param {Object} errors Types of errors
 * @returns {Object} { errors } field which is [string]
 */
const parseErrors = (errors) => {
  console.log('parse errors', errors);
  console.log('bad lines', errors.badLines.length);
  let errorList = [];
  if (errors.badPackages.length > 0) {
    errorList.push(`Found packages with errors.`);
    errorList.push(errors.badPackages[0]);
    return { errors: errorList };
  }
  if (errors.overLimit.length > 0) {
    errorList.push(`Cannot import because GAM line item limit would be exceeded by ${errors.overLimit[0]}`);
  }
  if (errors.badIds.length > 0) {
    errorList.push(`Following IDs don't belong to the order: ${errors.badIds.join(',')}`);
  }
  if (errors.badLines.length > 0) {
    errorList.push(`Found ${errors.badLines.length} placements with bad line errors`, errors.badLines.join(','));
    console.log(errors.badLines.length);
  }
  console.log(errorList);
  return { errors: errorList };
};

/**
 * Validate and process imported placements
 * First, find and process any packages
 * @param {Object} order Order
 * @param {Array} imports List of imported placements and packages
 * @param {Object} constants all the static values
 * @param strategies
 * @param deviceTypes
 * @param salesProducts
 * @param creativeProducts
 * @param presets
 * @param geos
 * @param importList list of fields to import
 * @returns {Object} {importErrors, imports, packages} fields
 */
const processImports = (order, imports, constants,
                         strategies, deviceTypes, salesProducts, creativeProducts, presets, geos, importList) => {
  const originals = order.Placements || [];
  let packages = [];
  const completedImports = [];
  const importErrors = {
    count: 0,
    badIds: [],
    badLines: [],
    overLimit: [],
    badPackages: []
  };
  // separate imports into packages and placements (0 and 1 respectively)
  const separatedImports = partition(imports, imp => imp.type && imp.type.slice(0,3) === 'PKG');

  if (separatedImports[0].length > 0) {
    importErrors.badPackages = validatePackages(separatedImports[0], constants, order);
    if (importErrors.badPackages.length > 0) {
      importErrors.count += 1;
      return { importErrors, imports: completedImports, packages: separatedImports[0] }
    } else {
      packages = separatedImports[0];
    }
  }
  separatedImports[1].forEach(imp => {
    const p = pick(imp, importList.map(il => il.field));
    const { errors, incomplete } = validatePlacement(order, p, constants, strategies, deviceTypes, salesProducts, creativeProducts, geos, presets)
    if (errors.length > 0) {
      importErrors.badLines.push(Object.values(p));
      importErrors.count += 1;
      imp.errors = errors.join();
    } else if (incomplete) {
      p.saveIncomplete = 1;
    }
    if (order.orderType !== 'Programmatic') {
      p.agencyPlacementId = removeLeadingUnderscore(p.agencyPlacementId);
      p.agencyBillingId = removeLeadingUnderscore(p.agencyBillingId);
    }
    if (p.id > 0) {  // update placement
      const oldPlacement = getOldPlacement(p, originals, order.id);
      if (!oldPlacement) {
        importErrors.badIds.push(p.id);
        importErrors.count += 1;
        imp.errors = 'placement id not on this order';
      } else {
        p.mode = 'update';
        p.saveIncomplete = 0;
        completedImports.push(setPlacementValues(order.id, p, constants, oldPlacement))
      }
    } else {  // insert placement
      p.mode = 'insert';
      completedImports.push(setPlacementValues(order.id, p, constants));
    }
  });
  const { itemCount: existingLineItemCount, itemLimit } = countLineItems(order);
  const updatedPlacementIds = new Set(
    separatedImports[1]
      .map(imp => parseInt(imp.id, 10))
      .filter(id => id > 0)
  );
  const replacedLineItemCount = originals
    .filter(placement => updatedPlacementIds.has(placement.id))
    .reduce((count, placement) => count + (Array.isArray(placement.LineItems)
      ? placement.LineItems.length
      : lineItemCountForSalesProduct(placement.SalesProduct)), 0);
  const importedLineItemCount = completedImports.reduce(
    (count, placement) => count + lineItemCountForSalesProduct(placement.SalesProduct, constants),
    0
  );
  const finalLineItemCount = existingLineItemCount - replacedLineItemCount + importedLineItemCount;
  if (finalLineItemCount > itemLimit) {
    importErrors.count += 1;
    importErrors.overLimit.push(finalLineItemCount - itemLimit);
  }
  return { importErrors, imports: completedImports, packages }
};

/**
 * If any placement or package is not valid we will produce an error file and go no further
 *
 * If it's all good:
 *   if there are packages, we save the packages using the package endpoint which also does placements
 *   otherwise we will save the placements using the placement endpoint
 * @param packages
 * @param constants
 * @param order
 */
const validatePackages = (packages, constants, order) => {
  const errors = [];
  /*
  const pkgNames = packages.map(pkg => pkg.agencyPackageName);
  const uniquePackageNames = [...new Set(pkgNames)];
  if (uniquePackageNames.length < pkgNames.length) {
    errors.push('Duplicate package names are not allowed');
    return errors;
  }
   */
  packages.forEach(pkg => {
    if (!pkg.agencyPackageName) {
      errors.push('Bad or missing package name');
    } else {
      pkg.name = pkg.agencyPackageName
    }
    pkg.ioRateType = 'CPM';
    pkg.contractedQuantity = checkNumber(pkg.contractedQuantity.replace(/\D/g,''));
    pkg.rate = checkNumber(pkg.ioRate.replace ( /[^0-9.]/g, '' ));
    pkg.IoId = order.id;
    pkg.rateType = pkg.ioRateType;
    pkg.cost = pkg.contractedQuantity * pkg.rate / 1000;  // just cpm
    pkg.comments = pkg.adNameComments;
  });
  return errors;
};

/**
 * First, standard targeting which is five inter-related fields
 * Then simple fields, some of which depend on targeting entities
 */
const validatePlacement = (order, p, constants, strategies, deviceTypes, salesProducts, creativeProducts, geos, presets) => {
  const { errors, incomplete } = validateStandardTargeting(order, p, strategies, deviceTypes, salesProducts, creativeProducts, presets, constants);
  console.log('validate special targeting', p);
  validateSpecialTargeting(p);
  if (typeof p.excludedGeotargetIds === 'string' && p.excludedGeotargetIds.trim() !== '') {
    try {
      const excludedIds = JSON.parse(p.excludedGeotargetIds);
      p.excludedGeotargetIds = Array.isArray(excludedIds)
        ? excludedIds.filter(id => id !== null && id !== undefined && id !== '')
        : [];
    } catch (e) {
      errors.push('Invalid excluded geotarget IDs');
      p.excludedGeotargetIds = [];
    }
  } else {
    p.excludedGeotargetIds = [];
  }
  let geoErrors;
  if (p.geoGlobal) {
    p.geotargets = 'Global';
    p.excludedGeotargetIds = [];
  } else if (isEmpty(geos)){
    p.excludedGeotargetIds = [];
  } else {
    const geoFields = [
      'states', 'dmas', 'provinces', 'territories',
      'neighborhoods', 'cities', 'counties', 'regions', 'countries'
    ];
    const hasGeoInput = geoFields.some(field =>
      typeof p[field] === 'string' && p[field].trim() !== ''
    );
    if (!hasGeoInput) {
      p.excludedGeotargetIds = [];
    }
    if (hasGeoInput) {
      try {
        geoErrors = validateGeotargets(order, p, constants, geos, errors);
      } catch(e) {
        console.log('error trying to validate geotargets', e, p.id, geoErrors);
      }
    }
  }
  if (p.pubs) {
    validatePubs(p, constants, errors);
  }
  if (p.exclusions){
    validateExclusions(p, presets, errors);
  }
  const requiredForImport = (order.orderType === 'Programmatic') ?
    ['agencyPlacementName', 'start', 'end'] :
    ['agencyPlacementId', 'agencyPlacementName', 'start', 'end'];
  requiredForImport.forEach(rfi => {
    if (!p[rfi]) {
      errors.push(`${rfi} missing`);
    }
  });
  if (!checkDate(p.start, 'start')) {
    errors.push(`invalid start`);
  }
  if (!checkDate(p.end, 'end')) {
    errors.push(`invalid end`);
  }
  p.IoRateType = constants.ioRateTypes.find(iort => iort.name.toLowerCase() === p.ioRateType.toLowerCase());
  if (!p.IoRateType) {
    p.IoRateType = { id: 1, name: 'CPM' };
    p.IoRateTypeId = 1;
  } else {
    p.IoRateTypeId = p.IoRateType.id;
  }
  p.creativePremiumPercent = checkNumber(p.creativePremiumPercent);
  if (!isEmpty(p.CreativeProducts) && p.CreativeProducts.some(cp => cp.premiumAssociated)) {
    if (p.creativePremiumPercent === 0) {
      errors.push('creativePremiumPercent is required');
    }
  }
  if (order.orderType !== 'Programmatic') {
    const viewability = constants.viewabilityTypes.find(vt => vt.toLowerCase() === p.viewability.toLowerCase());
    if (viewability) {
      p.viewability = viewability;
    } else {
      errors.push('Invalid viewability');
    }
  }
  if (!!order.packageType) {
    p.packageType = order.packageType;
  } else {
    const packageType = constants.packageTypes.find(pt => pt.toLowerCase() === p.packageType.toLowerCase());
    if (packageType) {
      p.packageType = packageType;
    } else {
      errors.push('Invalid package type');
    }
  }
  const languageChoices = manageLanguages(order);
  if (!p.targetLanguage) {
    p.targetLanguage = 'English';
  } else {
    const targetLanguage = languageChoices.find(lc => lc.toLowerCase() === p.targetLanguage.toLowerCase());
    if (targetLanguage) {
      p.targetLanguage = targetLanguage;
    } else {
      errors.push('Invalid target language');
    }
  }
  if (p.thirdPartyFees) {
    p.DataFees = [];
    const tp_list = p.thirdPartyFees.replace(/;/g, ',').split(',');
    tp_list.forEach(tp => {
      const [name, amount] = tp.split('=');
      const this_dft = constants.dataFeeTypes.find((dft) => {
        const vendor = dft.name.split(' - ')[1];
        return vendor.toLowerCase() === name.toLowerCase();
      })
      if (this_dft) {
        p.DataFees.push({ name: this_dft.name, amount });
      } else {
        errors.push(`Invalid data fee type ${name}`);
      }
    })
  }
  return { errors, incomplete };
};

/**
 * Each of the five main targeting fields needs to be valid (on the list) in itself
 * if so, then the combos (sp-cp, strat-sp, dt-cp) also need to be valid.
 * No targeting is allowed (incomplete) but if some targeting fields are present, they will all be validated.
 * There are a few special cases as well, including Programmatic and Canada.
 * Logically, this should match Targeting, which does the same checks for the UI.
 * The user sees ;-separated strings which are converted to/from objects
 * Upper-cased fields like p.Strategy are objects whereas p.strategy is a string
 * @param order
 * @param p  placement
 * @param strategies
 * @param deviceTypes
 * @param salesProducts
 * @param creativeProducts
 * @param presets
 * @param constants
 * @returns {{errors: *[], incomplete: number}}
 */
const validateStandardTargeting = (order, p, strategies, deviceTypes, salesProducts, creativeProducts, presets, constants) => {
  let incomplete = 0;
  const errors = [];
  let updatingDeliveringPlacement = false; //some edits don't apply when updating a delivering placement
  if (!!(p.id) && ['DELIVERING, READY'].includes(p.dfpStatus)) {
    updatingDeliveringPlacement = true;
  }
  if (!p.strategy && !p.salesProduct && !p.deviceTypes && !p.creativeProducts && !p.customCriteriaPresets) {
    incomplete = 1;
    return { errors, incomplete }
  } else if (!p.strategy || !p.salesProduct || !p.deviceTypes || !p.creativeProducts) {
    errors.push('Targeting attempted but incomplete');
    return { errors, incomplete }
  }
  p.Strategy = strategies.find(s => s.name.toLowerCase() === p.strategy.toLowerCase());
  if (!p.Strategy) {
    errors.push(`${p.strategy} invalid strategy`);
  } else {
    p.StrategyId = p.Strategy.id;
    if (order.Advertiser.countryCode === 'CDN' &&
      (p.Strategy.name.slice(0, 3) === 'ACT' || p.Strategy.name.slice(0, 3) === 'HDM')
    ) {
      errors.push(`${p.strategy} invalid strategy for Canada`);
    }
  }
  p.SalesProduct = spProgScrub(order, salesProducts).find(sp => sp.name.toLowerCase() === p.salesProduct.toLowerCase());
  if (!p.SalesProduct) {
    errors.push(`${p.salesProduct} invalid sales product, `);
  } else {
    p.SalesProductId = p.SalesProduct.id;
    if (order.orderType !== 'Programmatic' && p.SalesProduct.name === 'Hearst O&O Inventory') {
      errors.push(`${p.salesProduct} is only for Programmatic`);
    }
  }
  let this_presetTypeId;
  if (p.Strategy && p.SalesProduct) {
    this_presetTypeId = getPresetTypeId(p.Strategy, p.SalesProduct);
    if (this_presetTypeId && !p.customCriteriaPresets) {
      errors.push(`missing presets for strategy`);
    }
    if (!p.SalesProduct.Strategies.map(s => s.id).includes(p.Strategy.id)) {
      errors.push(`invalid strategy/sales product combo`);
    }
  }
  p.CustomCriteriaPresets = [];
  // Change ';' to ','. If there is whitespace, remove it. Change to lower case.
  if (this_presetTypeId) {
    const p_list = p.customCriteriaPresets.replace(/;/g, ',')
      .split(',')
      .map(p => p.toLowerCase().trim());
    p.CustomCriteriaPresets = presets.filter(p => (p_list.includes(p.name.toLowerCase()) &&
      p.CustomCriteriaPresetType.id === this_presetTypeId));
    // If the array lengths are different, there are errors, so display them
    if (p.CustomCriteriaPresets.length !== p_list.length) {
      const errorCount = p_list.length - p.CustomCriteriaPresets.length;
      const presetNames = p.CustomCriteriaPresets.map(preset => preset.name.toLowerCase());
      const misses = p_list.filter(pl => !presetNames.includes(pl));
      errors.push(`${errorCount} invalid standard targets ${misses.join()}`);
    }
  }
  const pdt_list = p.deviceTypes.replace(/;/g, ',')
    .split(',')
    .map(dt => dt.toLowerCase());
  p.DeviceTypes = deviceTypes.filter(dt => pdt_list.includes(dt.name.toLowerCase()));
  if (p.DeviceTypes.length !== pdt_list.length) {
    errors.push(`${p.deviceTypes} invalid device type`);
  }
  let pcp_list = p.creativeProducts.replace(/;/g, ',').split(',').map(cp => cp.toLowerCase());
  // Flag any invalid creative products for this Strategy/SalesProduct
  if (p.Strategy && p.SalesProduct) {
    const icps = constants.invalidCreativeProducts.filter(icp => icp.strat === p.Strategy.name && icp.sp === p.SalesProduct.name);
    icps.forEach(icp => {
      if (pcp_list.find(cp => cp === icp.cp.toLowerCase())) {
        errors.push(`invalid creative product ${icp.cp} for this strategy and sales product`);
      }
    });
  }
  p.CreativeProducts = cpProgScrub(order, creativeProducts).filter(cp => pcp_list.includes(cp.name.toLowerCase()));
  if (hasMixedVideoAndNonVideoProducts(p.CreativeProducts, constants.videoProducts)) {
    errors.push(mixedVideoAndNonVideoMessage);
  }
  if (updatingDeliveringPlacement) {
  } else {
    if (p.CreativeProducts.length !== pcp_list.length) {
      errors.push(`${p.creativeProducts} invalid creative product, `);
    } else {
      p.CreativeProducts.forEach(cp => {
        if (p.SalesProduct) {
          if (!cp.SalesProducts.map(sp => sp.id).includes(p.SalesProduct.id)) {
            errors.push(`invalid sales product ${p.SalesProduct.name} for creative product ${cp.name}`);
          }
        }
      });
      if (p.DeviceTypes && p.CreativeProducts) {
        p.DeviceTypes.forEach(pdt => {
          let found_product = false;
          let found_dts = [];
          p.CreativeProducts.forEach(cp => {
            if (cp.DeviceTypes.map(dt => dt.id).includes(pdt.id)) {
              found_product = true;
              found_dts.push(pdt.name);
            }
          });
          if (!found_product) {
            console.log('creative products', p.CreativeProducts);
            console.log('device types', p.DeviceTypes);
            errors.push(`missing creative product for device type ${pdt.name}`);
          } else {
            console.log('found dts', found_dts);
            console.log('found creative product for device type ', pdt.name);
            console.log('creative products', p.CreativeProducts);
            console.log('device types', p.DeviceTypes);
          }
        });
        p.CreativeProducts.forEach(cp => {
          let found_device = false;
          p.DeviceTypes.forEach(pdt => {
            if (cp.DeviceTypes.map(dt => dt.id).includes(pdt.id)) {
              found_device = true;
            }
          });
          if (!found_device) {
            errors.push(`missing device type for creative product ${cp.name}`);
          }
        });
      }
    }
  }
  return { errors, incomplete }
}

/**
 * For these combinations, if the required targeting is not found set a default custom target
 * @param p  placement
 * @returns nothing  - but placement.customTargetingArr may be updated by reference
 */
const validateSpecialTargeting = (p) => {
  console.log(p);
  if (!p.Strategy.name || !p.SalesProduct.name) {
    console.log('VST missing strategy or sales product', p.Strategy.name, p.SalesProduct.name);
    return;
  }
  console.log(`validate special targeting strategy:${p.Strategy.name} salesproduct: ${p.SalesProduct.name}`);
  if (p.Strategy.name === 'Custom Content' && p.SalesProduct.name === 'CDRT Contextual'){
    if (!p.customTargetingArr.includes('content')) {
      p.customTargetingArr = '[[{"type":"content","items":[{"name":"00000000","value":448052395406}],"qualifier":"included"}]]';
    }
  }
  if (p.Strategy.name === 'Custom Content' && p.SalesProduct.name === 'HDM Inventory'){
    if (!p.customTargetingArr.includes('artid')) {
      p.customTargetingArr = '[[{"type":"artid","items":[{"name":"00000000","value":448999499537}],"qualifier":"included"}]]';
    }
  }
  if (p.Strategy.name === 'Vehicle Engagement Platform' && p.SalesProduct.name === 'CDRT Contextual'){
    if (!p.customTargetingArr.includes('content')) {
      p.customTargetingArr = '[[{"type":"content","items":[{"name":"00000000","value":448052395406}],"qualifier":"included"}]]';
    }
  }
  if (p.Strategy.name === 'Vehicle Engagement Platform' && p.SalesProduct.name === 'HDM Inventory'){
    if (!p.customTargetingArr.includes('artid')) {
      p.customTargetingArr = '[[{"type":"artid","items":[{"name":"00000000","value":448999499537}],"qualifier":"included"}]]';
    }
  }
  console.log('ct array', p.customTargetingArr);
};

const spProgScrub = (order, sps) => {
  if (!order) return sps;
  if (order.orderType === 'Programmatic') {
    sps = sps.filter(sp => sp.code !== 'JCI' && sp.code !== 'JNU' && sp.code !== 'JNUI');
    return sps;
  }
  return sps.filter(sp => sp.name.slice(0, 4) !== 'PROG');
}

const cpProgScrub = (order, creativeProducts) => {
  if (!order) return creativeProducts;
  let filteredProducts = creativeProducts;
  if (order.orderType === 'Programmatic') {
    if (order.creativeSource === 'Advertiser Provided') {
      filteredProducts = creativeProducts.filter(cp => cp.progAdvProvided === 'Y');
    }
  } else {
    filteredProducts = creativeProducts.filter(cp => cp.progRule !== 'progOnly');
  }
  return filteredProducts;
};

/**
 * Some sales products require a list of publications
 * This is logically the same as stuff in Targeting and PubSelect
 * We need to populate from the real table because more than name may be needed (YouTube)
 * @param p - placement to validate
 * @param constants
 * @param errors - array of errors
 */
const validatePubs = (p, constants, errors) => {
  const selectType = hasPubSelect(p.SalesProduct, p.Strategy, !p.id);
  if (!selectType) {
    p.pubs = '';
    return;
  }
  let allPubs;
  if (selectType === 'AURA') {
    return;
  }
  if (selectType === 'AURA_TV') {
    allPubs = constants.pubsHDM;
  }
  if (selectType === 'CUSTOM') {
    allPubs = constants.pubsHOO.filter(ap => ap.name !== 'Gear Patrol');
    allPubs.push({ name: 'All Hearst Autos' });
  } else if (selectType === 'HDM') {
    allPubs = constants.pubsHDM;
  } else if (selectType === 'CDRT') {
    allPubs = constants.pubsCDRT;
  } else if (selectType === 'YT_HDM') {
    allPubs = constants.pubsYT_HDM;
  } else if (selectType === 'YT_CDRT') {
    allPubs = constants.pubsYT_CDRT;
  } else if (selectType === 'HOO') {
    allPubs = constants.pubsHOO;
  } else {
    return;
  }
  const pubNames = p.pubs.replace(/;/g, ',').split(',').map(pub => pub.toLowerCase());
  const errorPubs = [];
  const foundPubs = [];
  pubNames.forEach(pub => {
    if (allPubs.find(ap => ap.name.toLowerCase() === pub)) {
      foundPubs.push(pub);
    } else {
      errorPubs.push(pub);
    }
  });
  if (errorPubs.length > 0) {
    errors.push(`${errorPubs.join()} invalid publications`);
  } else {
    p.pubs = allPubs.filter(ap => foundPubs.includes(ap.name.toLowerCase()));
  }
};

/**
 * Only importing DMAs, States, Provinces, Territories, Cities, Neighborhoods, Counties, Regions, and Countries
 * [but not zip codes]
 * Cities and Counties use canonical name to handle duplicate names
 * As of May 2025, gets from database instead of constants
 * @param o = existing order
 * @param p = new or updated placement which is being validated
 * @param constants
 * @param geos
 * @param errors
 * Case-insensitive match of DMAs (210) and states(51 with DC) pulled from DB
 * Strip ",us" if present and replace any commas with single space
 */
const validateGeotargets = (o, p, constants, geos, errors) => {
  // console.log('validate geotargets');
  p.states = p.states.replace(/, us/g, '').replace(/,/g, ' ').replace(/ {2}/g, ' ');
  p.dmas = p.dmas.replace(/, us/g, '');
  const dmaList = p.dmas ? p.dmas.split(';').map(d => d.toLowerCase()) : [];
  const stateList = p.states ? p.states.split(';').map(s => s.toLowerCase()) : [];
  const provinceList = p.provinces ? p.provinces.split(';').map(p => p.toLowerCase()) : [];
  const territoryList = p.territories ? p.territories.split(';').map(t => t.toLowerCase()) : [];
  const neighborhoodList = p.neighborhoods ? p.neighborhoods.split(';').map(t => t.toLowerCase()) : [];
  const normalizeCanonicalGeoName = value => value.replace(/@/g, ', ').toLowerCase();
  const encodeCanonicalGeoName = value => value.replace(/, /g, '@');
  const cityList = p.cities ? p.cities.split(';').map(normalizeCanonicalGeoName) : [];
  const countyList = p.counties ? p.counties.split(';').map(normalizeCanonicalGeoName) : [];
  const regionList = p.regions ? p.regions.split(';').map(t => t.toLowerCase()) : [];
  const countryList = p.countries ? p.countries.split(';').map(t => t.toLowerCase()) : [];
  if (dmaList.length === 0 && stateList.length === 0 && provinceList.length === 0 &&
    territoryList.length === 0 && neighborhoodList.length === 0 && cityList.length === 0 &&
    countyList.length === 0 &&
    regionList.length === 0 && countryList.length === 0 ) {
    p.geotargets = '';
    return;
  }
  let geoObj;
  geoObj = geos.find(g => g.name === 'states');
  const foundStates = geoObj.items.filter(s => stateList.includes(s.toLowerCase()));
  if (foundStates.length !== stateList.length) {
    errors.push(`${stateList} invalid state`);
  }
  geoObj = geos.find(g => g.name === 'provinces');
  const foundProvinces = geoObj.items.filter(s => provinceList.includes(s.toLowerCase()));
  if (foundProvinces.length !== provinceList.length) {
    errors.push(`${provinceList} invalid province`);
  }
  geoObj = geos.find(g => g.name === 'territories');
  const foundTerritories = geoObj.items.filter(s => territoryList.includes(s.toLowerCase()));
  if (foundTerritories.length !== territoryList.length) {
    errors.push(`${territoryList} invalid territory`);
  }
  if (o.Advertiser.countryCode === 'CDN') {
    geoObj = geos.find(g => g.name === 'CA_neighborhoods');
  } else {
    geoObj = geos.find(g => g.name === 'US_neighborhoods');
  }
  const foundNeighborhoods = geoObj.items.filter(s => neighborhoodList.includes(s.toLowerCase()))
  if (foundNeighborhoods.length !== neighborhoodList.length) {
    errors.push(`${neighborhoodList} invalid neighborhood`);
  }
  if (o.Advertiser.countryCode === 'CDN') {
    geoObj = geos.find(g => g.name === 'CA_cities');
  } else {
    geoObj = geos.find(g => g.name === 'US_cities');
  }
  const foundCities = geoObj.items.filter(s => cityList.includes(normalizeCanonicalGeoName(s)))
  if (foundCities.length !== cityList.length) {
    errors.push(`${cityList} invalid city`);
  }
  geoObj = geos.find(g => g.name === 'counties');
  const foundCounties = geoObj
    ? geoObj.items.filter(s => countyList.includes(normalizeCanonicalGeoName(s)))
    : [];
  if (foundCounties.length !== countyList.length) {
    errors.push(`${countyList} invalid county`);
  }
  geoObj = geos.find(g => g.name === 'regions');
  const foundRegions = geoObj.items.filter(s => regionList.includes(s.toLowerCase()));
  if (foundRegions.length !== regionList.length) {
    errors.push(`${regionList} invalid region`);
  }
  geoObj = geos.find(g => g.name === 'countries');
  const foundCountries = geoObj.items.filter(s => countryList.includes(s.toLowerCase()));
  if (foundCountries.length !== countryList.length) {
    errors.push(`${countryList} invalid country`);
  }
  geoObj = geos.find(g => g.name === 'dmas');
  const errorDMAs = [];
  const foundDMAs = [];
  dmaList.forEach(dma => {
    if (geoObj.items.find(g => g.toLowerCase() === dma)) {
      foundDMAs.push(dma);
    } else {
      errorDMAs.push(dma);
    }
  });
  if (errorDMAs.length > 0) {
    errors.push(`${errorDMAs.join()} invalid dmas`);
  } else {
    const encodedCities = foundCities.map(encodeCanonicalGeoName);
    const encodedCounties = foundCounties.map(encodeCanonicalGeoName);
    p.geotargets = `DMA=${foundDMAs.join(';')}+STATE=${foundStates}+PROVINCE=${foundProvinces}+TERRITORY=${foundTerritories}+NEIGHBORHOOD=${foundNeighborhoods}+CITY=${encodedCities}+COUNTY=${encodedCounties}+REGION=${foundRegions}+COUNTRY=${foundCountries}`;
  }
  return errors;
}

const validateExclusions = (p, presets) => {
  const excNames = p.exclusions.replace(/;/g, ',').split(',').map(exc => exc.toLowerCase());
  const modelPresets = presets.filter(pre => pre.CustomCriteriaPresetType.id === 3);
  const makePresets = presets.filter(pre => pre.CustomCriteriaPresetType.id === 5);
  const modelExclusions = modelPresets.filter(mp => excNames.includes(mp.name.toLowerCase()));
  const makeExclusions = makePresets.filter(mp => excNames.includes(mp.name.toLowerCase()));
  p.exclusions = { makes: makeExclusions, models: modelExclusions };
}

const setPlacementValues = (ioId, p, constants, old) => {
  p.contractedQuantity = checkNumber(p.contractedQuantity.replace(/\D/g,''));
  p.ioRate = checkNumber(p.ioRate.replace ( /[^0-9.]/g, '' ));
  p.impressionsLimit = checkNumber(p.impressionsLimit);
  p.start = moment(p.start).set({ h: 9, m: 0 }).format();  //ToDo: fix  date
  p.end = moment(p.end).set({ h: 9, m: 0 }).format();
  p.start = moment.tz(p.start, 'America/New_York').startOf('day').format();
  if (Date.parse(p.end) < Date.parse(p.start) ) {
    p.end = p.start;
  }
  p.IoId = ioId;
  if (p.mode !== 'insert' && old && old.LineItems.length > 0 &&
    old.LineItems[0].adManagerLineitemId && isDelivering(old)) {
    delete p.start;
  }
  if (!p.exclusions) {
    p.exclusions = [];
  }
  if (!p.pubs) {
    p.pubs = [];
  }
  if (p.customTargetingArr) {
    p.customTargetingArr = JSON.parse(p.customTargetingArr);
  }
  return p;
};

/**
 * Checks that ids are valid and that cookies haven't been messed up on a given placement
 * @param {Object} imp Imported Placement
 * @param {Array} originals Original Placements
 * @param {int} orderId
 * @returns {Object} Original placement
 */
const getOldPlacement = (imp, originals, orderId) => {
  imp.id = parseInt(imp.id, 10);
  const oldPlacement = originals.find(op => imp.id === op.id)
  const oldCookie = hashCode(orderId.toString() + imp.id.toString()).toString();
  const newCookie = imp.cookie.toString();
  if (newCookie === oldCookie) {
    return oldPlacement;
  }
  return false;
};

const getPresetTypeId = (strategy, salesProduct) => {
  if (salesProduct.name === 'JAG Awareness' && strategy.name === 'Suggestive') {
    return null;
  }
  return strategy.RequiredDetailTypeId;
};

const manageLanguages = (order) => {
  const languageChoices = ['English'];
  if (order && order.Advertiser && order.Advertiser.countryCode === 'CDN') {
    languageChoices.push('French Browser');
  } else {
    languageChoices.push('Spanish Browser');
  }
  return languageChoices;
};

const removeLeadingUnderscore = s => (s.slice(0, 1) === '_') ? s.slice(1) : s;

const checkNumber = (n) => (!isNaN(parseFloat(n)) && isFinite(n)) ? n : 0; // if number is not valid, make it zero

// const checkBoolean = b => (b && b === 1) ? 1 : 0;

const checkDate = (d) => {
  const dx = moment(d, ['YYYY-MM-DD', 'MM/DD/YY']).format('YYYY-MM-DD');
  return moment(dx, 'YYYY-MM-DD', true).isValid();
}
